import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { web as web2026, web2025 } from "@/config/System";
import {
  ArrowRight,
  BookOpen,
  GraduationCap,
  Medal,
  School,
  Sparkles,
} from "lucide-react";
import { Link, useSearchParams } from "react-router-dom";

import ImgSNBP from "@/assets/logos/snbp.png";
import ImgSNBT from "@/assets/logos/snbt.png";

// Mapping tema warna agar lebih rapi dan mudah di-maintenance
const themeStyles: Record<
  string,
  {
    cardHover: string;
    badge: string;
    iconBg: string;
    accent: string;
    button: string;
  }
> = {
  blue: {
    cardHover:
      "hover:border-sky-300 dark:hover:border-sky-700 hover:shadow-[0_8px_30px_rgb(14,165,233,0.15)]",
    badge:
      "bg-sky-100 text-sky-700 border-sky-200 dark:bg-sky-500/10 dark:text-sky-400 dark:border-sky-500/20",
    iconBg: "bg-sky-50 dark:bg-sky-500/10 text-sky-600 dark:text-sky-400",
    accent: "bg-sky-500 dark:bg-sky-400",
    button:
      "hover:bg-sky-50 text-sky-700 dark:text-sky-300 dark:hover:bg-sky-500/10 dark:hover:text-sky-300",
  },
  red: {
    cardHover:
      "hover:border-rose-300 dark:hover:border-rose-700 hover:shadow-[0_8px_30px_rgb(244,63,94,0.15)]",
    badge:
      "bg-rose-100 text-rose-700 border-rose-200 dark:bg-rose-500/10 dark:text-rose-400 dark:border-rose-500/20",
    iconBg: "bg-rose-50 dark:bg-rose-500/10 text-rose-600 dark:text-rose-400",
    accent: "bg-rose-500 dark:bg-rose-400",
    button:
      "hover:bg-rose-50 text-rose-700 dark:text-rose-300 dark:hover:bg-rose-500/10 dark:hover:text-rose-300",
  },
  indigo: {
    cardHover:
      "hover:border-indigo-300 dark:hover:border-indigo-700 hover:shadow-[0_8px_30px_rgb(99,102,241,0.15)]",
    badge:
      "bg-indigo-100 text-indigo-700 border-indigo-200 dark:bg-indigo-500/10 dark:text-indigo-400 dark:border-indigo-500/20",
    iconBg:
      "bg-indigo-50 dark:bg-indigo-500/10 text-indigo-600 dark:text-indigo-400",
    accent: "bg-indigo-500 dark:bg-indigo-400",
    button:
      "hover:bg-indigo-50 text-indigo-700 dark:text-indigo-300 dark:hover:bg-indigo-500/10 dark:hover:text-indigo-300",
  },
  slate: {
    cardHover:
      "hover:border-slate-400 dark:hover:border-slate-600 hover:shadow-[0_8px_30px_rgb(100,116,139,0.15)]",
    badge:
      "bg-slate-100 text-slate-700 border-slate-200 dark:bg-slate-500/10 dark:text-slate-300 dark:border-slate-500/20",
    iconBg: "bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-400",
    accent: "bg-slate-600 dark:bg-slate-400",
    button:
      "hover:bg-slate-100 text-slate-700 dark:text-slate-300 dark:hover:bg-slate-800 dark:hover:text-slate-300",
  },
};

export default function JalurSeleksiModern() {
  const [searchParams] = useSearchParams();
  const tahunParams = searchParams.get("tahun") || 2026;

  let web;
  switch (String(tahunParams)) {
    case "2025":
      web = web2025;
      break;
    case "2026":
      web = web2026;
      break;
    default:
      web = web2026;
      break;
  }

  const listJalur = [
    {
      id: "snbp",
      image: ImgSNBP,
      title: "SNBP",
      longTitle: "Seleksi Nasional Berbasis Prestasi",
      subtitle: "Diploma & Sarjana",
      description:
        "Jalur undangan nasional berdasarkan nilai akademik rapor dan prestasi siswa. Tanpa tes tertulis.",
      type: "Nasional",
      theme: "blue",
      icon: <Medal className="w-4 h-4" />,
      link: "https://undiksha.ac.id/pmb/tahun2026/snbp/",
    },
    {
      id: "snbt",
      image: ImgSNBT,
      title: "SNBT",
      longTitle: "Seleksi Nasional Berbasis Tes",
      subtitle: "Diploma & Sarjana",
      description:
        "Jalur seleksi nasional berdasarkan hasil Ujian Tulis Berbasis Komputer (UTBK).",
      type: "Nasional",
      theme: "red",
      icon: <BookOpen className="w-4 h-4" />,
      link: "https://undiksha.ac.id/pmb/tahun2026/snbt/",
    },
    {
      id: "mandiri-s1",
      image: web?.logo,
      title: "SMBJM Sarjana",
      longTitle: "Seleksi Mandiri (SMBJM)",
      subtitle: "Diploma & Sarjana",
      description: `Seleksi mandiri resmi ${web?.univAcronym} melalui Jalur CBT, Prestasi, Talent Scouting, Skor UTBK, Kepemimpinan, dan Sekolah Lab.`,
      type: "Mandiri",
      theme: "indigo",
      icon: <School className="w-4 h-4" />,
      link: "https://undiksha.ac.id/pmb/tahun2026/smbjm/",
    },
    {
      id: "mandiri-s2",
      image: web?.logo,
      title: "Pascasarjana",
      longTitle: "Seleksi Pascasarjana",
      subtitle: "Magister & Doktor",
      description:
        "Jalur khusus bagi calon mahasiswa yang ingin melanjutkan studi ke jenjang Magister (S2) atau Doktor (S3).",
      type: "Mandiri",
      theme: "slate",
      icon: <GraduationCap className="w-4 h-4" />,
      link: "https://undiksha.ac.id/penerimaan-mahasiswa-baru-pascasarjana-tahun-akademik-2026-2027/",
    },
  ];

  return (
    <section
      id="jalur-seleksi"
      className="relative py-24 overflow-hidden transition-colors duration-300"
    >
      {/* Decorative Background Blobs */}
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-full max-w-3xl h-[500px] bg-blue-500/5 dark:bg-blue-500/10 blur-[100px] rounded-full pointer-events-none" />

      <div>
        {/* Header Section */}
        <div className="text-center max-w-3xl mx-auto mb-16 space-y-5">
          <Badge
            variant="outline"
            className="border-blue-200 text-blue-700 bg-blue-50 dark:bg-blue-900/30 dark:border-blue-800 dark:text-blue-400 px-4 py-1.5 rounded-full text-sm font-medium shadow-sm"
          >
            <Sparkles className="w-4 h-4 mr-2 inline-block" />
            Tahun Akademik {tahunParams}/{Number(tahunParams) + 1}
          </Badge>
          <h1 className="text-4xl md:text-5xl font-extrabold text-slate-900 dark:text-white tracking-tight">
            Jalur Penerimaan Mahasiswa
          </h1>
          <p className="text-lg text-slate-600 dark:text-slate-400 leading-relaxed max-w-2xl mx-auto">
            {web?.univAcronym} membuka berbagai jalur seleksi untuk menjaring
            putra-putri terbaik bangsa. Temukan jalur yang paling sesuai dengan
            kualifikasi Anda.
          </p>
        </div>

        {/* Grid Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {listJalur.map((item, idx) => {
            const style = themeStyles[item.theme] || themeStyles.blue;

            return (
              <Card
                key={idx}
                className={`group relative flex flex-col h-full overflow-hidden transition-all duration-500 border-slate-200/60 dark:border-slate-800/60 bg-white/80 dark:bg-slate-900/80 backdrop-blur-xl shadow-sm hover:-translate-y-1.5 ${style.cardHover}`}
              >
                {/* Top Accent Line */}
                <div
                  className={`absolute top-0 left-0 w-full h-1.5 opacity-80 transition-transform origin-left scale-x-75 group-hover:scale-x-100 duration-500 ${style.accent}`}
                />

                {/* Card Header (Logo & Type) */}
                <div className="p-6 pb-4 flex justify-between items-start">
                  <div
                    className={`w-14 h-14 p-2.5 rounded-2xl flex items-center justify-center transition-transform duration-500 group-hover:scale-110 group-hover:-rotate-3 ${style.iconBg}`}
                  >
                    <img
                      src={item.image}
                      alt={item.title}
                      className="w-full h-full object-contain drop-shadow-sm"
                    />
                  </div>
                  <Badge
                    variant="outline"
                    className={`font-semibold px-3 py-1 border ${style.badge}`}
                  >
                    {item.type}
                  </Badge>
                </div>

                <CardHeader className="pt-2 pb-2">
                  <div className="flex items-center gap-2 text-sm font-bold tracking-wide mb-2 uppercase opacity-80">
                    <span className={`p-1.5 rounded-lg ${style.iconBg}`}>
                      {item.icon}
                    </span>
                    <span
                      className={`bg-clip-text text-transparent bg-gradient-to-r from-slate-700 to-slate-500 dark:from-slate-200 dark:to-slate-400`}
                    >
                      {item.subtitle}
                    </span>
                  </div>
                  <CardTitle className="text-xl font-bold text-slate-900 dark:text-white leading-tight">
                    {item.longTitle}
                  </CardTitle>
                </CardHeader>

                <CardContent className="flex-1">
                  <CardDescription className="text-slate-600 dark:text-slate-400 text-sm leading-relaxed">
                    {item.description}
                  </CardDescription>
                </CardContent>

                <CardFooter className="pt-4 pb-6 mt-auto">
                  <Button
                    asChild
                    variant="ghost"
                    className={`w-full justify-between border border-transparent group/btn transition-all duration-300 font-medium ${style.button}`}
                  >
                    <Link
                      to={`/informasi-jalur?embedUrl=${item.link}`}
                      target="_blank"
                    >
                      Pelajari Selengkapnya
                      <ArrowRight className="w-4 h-4 ml-2 transition-transform duration-300 group-hover/btn:translate-x-1.5" />
                    </Link>
                  </Button>
                </CardFooter>
              </Card>
            );
          })}
        </div>
      </div>
    </section>
  );
}
